import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';

const VRTViewport = ({ studyInstanceUID, seriesInstanceUID, slotLabel = '' }) => {
  const [preset, setPreset] = useState('bone');
  const [loading, setLoading] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [azimuth, setAzimuth] = useState(30);
  const [elevation, setElevation] = useState(20);
  const dragging = useRef(false);
  const dragStart = useRef(null);
  const dragTimer = useRef(null);
  const dragRequestCount = useRef(0);
  const lastProcessedRequest = useRef(0);

  useEffect(() => {
    if (!studyInstanceUID || !seriesInstanceUID) return;
    setLoading(true);
    const url = `/render/study/${studyInstanceUID}/series/${seriesInstanceUID}/vrt?preset=${preset}&az=${azimuth}&el=${elevation}&width=512&height=512`;
    const img = new window.Image();
    img.onload = () => { setImgSrc(url); setLoading(false); };
    img.onerror = () => setLoading(false);
    img.src = url;
  }, [studyInstanceUID, seriesInstanceUID, preset, azimuth, elevation]);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, az: azimuth, el: elevation };
    e.preventDefault();
  }, [azimuth, elevation]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !dragStart.current) return;
      if (dragTimer.current) clearTimeout(dragTimer.current);
      const newAz = dragStart.current.az + (e.clientX - dragStart.current.x) * 0.5;
      const newEl = Math.max(-80, Math.min(80, dragStart.current.el - (e.clientY - dragStart.current.y) * 0.5));
      dragTimer.current = setTimeout(() => {
  dragRequestCount.current++;
  const currentRequest = dragRequestCount.current;
  if (currentRequest <= lastProcessedRequest.current) return;
  lastProcessedRequest.current = currentRequest;
  const url = `/render/study/${studyInstanceUID}/series/${seriesInstanceUID}/vrt?preset=${preset}&az=${newAz.toFixed(1)}&el=${newEl.toFixed(1)}&width=256&height=256`;
  const img = new window.Image();
  img.onload = () => {
    if (currentRequest >= lastProcessedRequest.current) setImgSrc(url);
  };
  img.src = url;
}, 80);
    };
    const onUp = (e) => {
      if (!dragging.current) return;
      dragging.current = false;
      if (dragTimer.current) { clearTimeout(dragTimer.current); dragTimer.current = null; }
      if (!dragStart.current) return;
      const newAz = dragStart.current.az + (e.clientX - dragStart.current.x) * 0.5;
      const newEl = Math.max(-80, Math.min(80, dragStart.current.el - (e.clientY - dragStart.current.y) * 0.5));
      setAzimuth(newAz);
      setElevation(newEl);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [studyInstanceUID, seriesInstanceUID, preset]);

  if (!studyInstanceUID || !seriesInstanceUID) {
    return (
      <Box sx={{ width: '100%', height: '100%', bgcolor: '#080808', border: '1px solid #1e1e1e',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ color: '#222', fontSize: 32 }}>⊕</Typography>
        <Typography sx={{ color: '#2a2a2a', fontSize: 10, letterSpacing: 3 }}>DROP SERIES FOR VRT</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', bgcolor: '#000', border: '1px solid #2a2a2a',
      display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
      cursor: 'grab', userSelect: 'none' }} onMouseDown={onMouseDown}>
      <Box sx={{ position: 'absolute', top: 4, left: 6, zIndex: 10, pointerEvents: 'none' }}>
        <Typography sx={{ color: '#00bcd4', fontSize: 10, fontFamily: 'monospace' }}>VRT 3D</Typography>
      </Box>
      {slotLabel && (
        <Box sx={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', zIndex: 10, pointerEvents: 'none' }}>
          <Typography sx={{ color: '#444', fontSize: 8, letterSpacing: 2 }}>{slotLabel}</Typography>
        </Box>
      )}
      <Box sx={{ position: 'absolute', top: 4, right: 6, zIndex: 10, pointerEvents: 'none' }}>
        <Typography sx={{ color: '#333', fontSize: 8 }}>drag to rotate</Typography>
      </Box>
      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.7)', zIndex: 5, flexDirection: 'column', gap: 1 }}>
          <CircularProgress size={24} sx={{ color: '#00bcd4' }} />
          <Typography sx={{ color: '#666', fontSize: 10 }}>Building 3D volume…</Typography>
        </Box>
      )}
      {imgSrc && (
        <img src={imgSrc} alt="VRT" draggable={false}
          style={{ width: '100%', height: 'calc(100% - 36px)', objectFit: 'contain', display: 'block' }} />
      )}
      <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
        display: 'flex', gap: 0.5 }}>
        {['bone', 'soft', 'mip'].map(p => (
          <Button key={p} size="small" variant={preset === p ? 'contained' : 'outlined'}
            onClick={(e) => { e.stopPropagation(); setPreset(p); }}
            sx={{ fontSize: 9, py: 0.2, px: 0.8, minWidth: 0, borderColor: '#333',
              color: preset === p ? '#000' : '#666', '&.MuiButton-contained': { bgcolor: '#00bcd4' } }}>
            {p.toUpperCase()}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default VRTViewport;
